import { Form, Formik } from "formik";
import {
  BACK_BUTTON_TEXT,
  NEXT_BUTTON_TEXT,
  CURRENT_COVERAGE_FORM,
  LOADING_BUTTON_TEXT,
} from "pages/PreQualification/utils/constants";
import { useEffect, useState } from "react";
import Alert from "uicomponents/Alert";
import FooterButton from "uicomponents/FooterButton";
import styles from "./CurrentCoverage.module.css";
import CurrentCoverageComponent from "./component/CurrentCoverageComponent";
import { CurrentCoverageType } from "types";
import { CurrentCoverageFormValues } from "pages/PreQualification/utils/initialFormValues";
import { currentCoverageValidationSchema } from "pages/PreQualification/utils/validationSchema";
import { usePreQualContext } from "pages/PreQualification/PreQualification";
import { CurrentCoverageFormType } from "pages/PreQualification/utils/formTypes";
import {
  convertStringIntoBool,
  ConvertToISODate,
  errorMessageMap,
} from "utils/helper";
import {
  trackFormError,
  trackPageViewUserStep,
  trackSaveAndContinueUserStep,
} from "utils/analytics/trackEvent";
import {
  useUpdateCurrentCoverageMutation,
  useUpdateStepMutation,
} from "types/generated/operations";
import { getProspectUuid } from "utils/idHelpers";
import { HealthcareCarrierTypeEnum } from "types/generated/schema";
import Stepper from "uicomponents/Stepper";
import { getStepNumberFromURL } from "utils/helper";
import newstyles from "../../PreQualification.module.css";
import { STEPS } from "pages/PreQualification/utils/constants";
import Error, { EErrorType } from "uicomponents/Error";

interface ICurrentCoverageProps {
  data: CurrentCoverageType;
}

const CurrentCoverage = (props: ICurrentCoverageProps) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageViewUserStep(2, "current coverage");
  }, []);

  const { data } = props;
  const isNotEligibleForCoverage =
    data?.healthInsuranceInformation?.isCoverageEligible === false;

  const { previousStep, nextStep, stepperNextStep, isEligibleForCoverage } =
    usePreQualContext();

  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const initialFormValues = CurrentCoverageFormValues(data);
  const [updateCurrentCoverage, { loading: updateCurrentCoverageLoading }] =
    useUpdateCurrentCoverageMutation();
  const [updateStep, { loading: updateStepLoading }] = useUpdateStepMutation();

  const applicationStep = data?.applications?.[0]?.step;
  const currentStep = getStepNumberFromURL();
  const isInValidStep = applicationStep && currentStep > applicationStep;

  const handleSubmit = async (values: CurrentCoverageFormType) => {
    try {
      const currentStep = getStepNumberFromURL();
      await updateCurrentCoverage({
        variables: {
          uuid: getProspectUuid(),
          cobraUsage: values.cobraUsage
            ? convertStringIntoBool(values.cobraUsage)
            : null,
          currentlyOfferHealthInsurance: values.currentlyOfferHealthInsurance
            ? convertStringIntoBool(values.currentlyOfferHealthInsurance)
            : null,
          currentCarrier:
            (values.currentCarrier as HealthcareCarrierTypeEnum) || null,
          currentCarrierOther: values.currentCarrierOther,
          currentRenewalDate: values.currentRenewalDate
            ? ConvertToISODate(values.currentRenewalDate)
            : null,
          cobraUsers: Number(values.cobraUsers),
          benefitEligibleEmployees: Number(values.benefitEligibleEmployees),
        },
      });
      if (data?.applications?.[0]?.step === currentStep) {
        await updateStep({
          variables: {
            uuid: data?.applications?.[0].uuid,
            step: data?.applications?.[0]?.step + 1,
          },
        });
        trackSaveAndContinueUserStep(currentStep, "Current Coverage");
      }
      nextStep();
    } catch (error: any) {
      const errorMessage: string = error?.message;
      errorMessageMap[errorMessage] &&
        setErrorMessage(errorMessageMap[errorMessage]);
      trackFormError(error, errorMessage);
      setIsError(true);
      return;
    }
  };

  if (isInValidStep) {
    return <Error />;
  }

  if (isNotEligibleForCoverage && (data?.applications?.[0].step ?? 0) > 2) {
    isEligibleForCoverage && isEligibleForCoverage(isNotEligibleForCoverage);
    return <Error type={EErrorType.NotEligibleForCoverage} />;
  }

  return (
    <div className={newstyles.prequalformContainer}>
      <Stepper
        steps={STEPS}
        currentStep={getStepNumberFromURL()}
        status={data?.applications?.[0].step || 2}
        onClick={(stepNumber) => stepperNextStep(stepNumber)}
      />
      <div className={newstyles.prequalformDiv}>
        <div className={styles.title}>{CURRENT_COVERAGE_FORM.TITLE}</div>
        <Formik
          initialValues={initialFormValues}
          validationSchema={currentCoverageValidationSchema}
          onSubmit={handleSubmit}
        >
          {() => (
            <Form>
              <CurrentCoverageComponent />
              <FooterButton
                nextButtonText={
                  updateCurrentCoverageLoading || updateStepLoading
                    ? LOADING_BUTTON_TEXT
                    : NEXT_BUTTON_TEXT
                }
                backButtonText={BACK_BUTTON_TEXT}
                disabled={updateCurrentCoverageLoading || updateStepLoading}
                handleBackClick={previousStep}
              />
              {isError && <Alert message={errorMessage} />}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default CurrentCoverage;
